// BULK REQUEST INTERVIEWS ************************************************************************
// pathname: /v1/resume-operation/job/{jobSlug}/bulk-request-interviews
// ************************************************************************************************
import { useSwrMutationApi } from 'src/hooks/useSwrApi';
import { BulkRequestInterviewsResponse, PostBulkRequestInterviewsObject } from '@type/v1-api-types';

/**
 * @description Bulk request interviews for selected applicants of a job.
 *
 * @param jobSlug - the job slug
 * @param locale - the locale
 * @param onSuccess - callback function to be executed on success. Receives the
 *                    typed response so the caller can react to per-bucket
 *                    counts (notably `insufficientBalance`, the per-applicant
 *                    validations the backend skipped after the account hit the
 *                    negative-balance floor mid-batch).
 * @param onError - callback function to be executed on error
 *
 * @example
 * const { trigger, isMutating } = useSwrBulkRequestInterviews({ jobSlug: 'job-slug', locale: 'ro' });
 * trigger();
 */
export const useSwrBulkRequestInterviews = (props: {
  jobSlug: string;
  locale?: string;
  onSuccess?: (response: BulkRequestInterviewsResponse | undefined) => void;
  onError?: () => void;
  /**
   * Default `true` for backwards-compat with the existing per-CV callers.
   * Set `false` when the caller owns messaging (e.g. bulk-validate needs
   * to say "N validated / M skipped" and can't accept the generic auto
   * "success" toast on top).
   */
  showSuccessToast?: boolean;
}) => {
  const {
    jobSlug,
    locale = 'ro',
    onSuccess,
    onError,
    showSuccessToast = true,
  } = props;

  const {
    data,
    error,
    trigger,
    isMutating,
    reset,
  } = useSwrMutationApi<BulkRequestInterviewsResponse, PostBulkRequestInterviewsObject>({
    url: `/v1/resume-operation/job/${jobSlug}/bulk-request-interviews`,
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    apiOptions: {
      onSuccess: (response) => {
        if (onSuccess) onSuccess(response);
      },
      onError: () => {
        if (onError) onError();
      },
    },
    toastsOptions: {
      showSuccessToast,
      showErrorToast: true,
    },
  });

  return {
    data,
    error,
    trigger,
    isMutating,
    reset,
  };
};
